import { motion, useReducedMotion, type HTMLMotionProps } from 'framer-motion';
import { Children, type ReactNode } from 'react';

/** Press feedback for anything tappable: 0.96 over 150 ms, ease-out, no hover scale (docs/DESIGN.md). */
export const PRESS = { whileTap: { scale: 0.96 }, transition: { duration: 0.15, ease: 'easeOut' } } as const;

/** A sticker's tap: the same press plus a small tilt (interest stickers, chips). */
export const STICKER_TAP = { whileTap: { scale: 0.96, rotate: -3 }, transition: { duration: 0.15, ease: 'easeOut' } } as const;

/** Rises into place on mount (opacity, 4 px blur, 12 px, 300 ms). Use `i` to stagger siblings 100 ms apart. */
export function FloatIn({ i = 0, children, className, ...rest }: { i?: number; children: ReactNode; className?: string } & HTMLMotionProps<'div'>) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 12, filter: 'blur(4px)' }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      transition={{ duration: reduced ? 0.15 : 0.3, ease: 'easeOut', delay: reduced ? 0 : 0.1 * i }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/** Staggers each child in. */
export function Stagger({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={className}>
      {Children.toArray(children).map((c, i) => (
        <FloatIn key={i} i={i}>
          {c}
        </FloatIn>
      ))}
    </div>
  );
}
